// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @types
import {
  GetConstantsQueryResult,
  GetUserQuery,
  GetUserQueryResult,
} from 'lib/graphql/graphqlTypes'
import { Direction, NavigationContext } from 'modules/common/context/navigation'
import { EducationFormValues } from 'modules/common/forms/types/education'
// @libraries
import { DevTool } from '@hookform/devtools'
import { makeStyles } from '@material-ui/core/styles'
// @common
// @graphQL
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module component
import Navigation from 'modules/onBoarding/components/_common/Navigation'
import EducationFields from 'modules/common/forms/fields/education/Education'
import useFormWrapper from 'modules/common/forms/hooks/useEducationFormWrapper'
import useNavigation from 'modules/onBoarding/hooks/useNavigation'
import { Slide } from '@material-ui/core'
import { ApolloQueryResult } from '@apollo/client'

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& form': {
      width: '100%',
    },
  },
  formContainer: {
    position: 'relative',
  },
}))

type SelfProps = {
  data?: GetUserQueryResult['data']
  constants: GetConstantsQueryResult['data']
  isLoading: boolean
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}
type PropsType = SelfProps

const EducationForm = ({
  data: userData,
  constants: constantsData,
  isLoading,
  refetch,
}: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [slidingIn, setSlidingIn] = useState<boolean>(false)
  const [destination, setDestination] = useState<string>('')
  const [direction, setDirection] = useState<Direction>(Direction.LEFT)

  /******************************************
   * HOOKS
   ******************************************/
  const classes = useStyles()
  const navigation = useContext(NavigationContext)
  const history = useHistory()

  useEffect(() => {
    // if (1 || !isLoading && !!userData && !slidingIn) {
    //   setSlidingIn(true)
    // }
    setSlidingIn(true)
  }, [])

  const {
    formMethods,
    control,
    userEducationDeleteHandler,
    userEducationUpdateHandler,
  } = useFormWrapper({
    data: userData,
    isLoading,
    refetch,
  })
  // React Hook Form
  const {
    formState: { dirtyFields },
  } = formMethods
  const isFormDirty = Object.keys(dirtyFields).length > 0

  const { handleNextClick, handleSaveExit, handlePrevClick } = useNavigation({
    setDestination,
    setDirection,
    setSlidingIn,
    setIsSubmitting,
    isFormDirty,
    updateHandler: userEducationUpdateHandler,
  })

  /**
   *
   * @param id
   * @param remove
   */
  const onRemoveHandler = async (
    id: number,
    remove: (index?: number | number[]) => void,
  ) => {
    setIsSubmitting(true)
    await userEducationDeleteHandler(id, remove)
    setIsSubmitting(false)
  }

  /******************************************
   * RENDER
   ******************************************/

  const box = (content: ReactElement, isLoading: boolean) => {
    return <Box>{!isLoading ? content : <SkeletonLoader />}</Box>
  }

  return (
    <>
      {process.env.DEBUG !== 'true' && <DevTool control={control} />}
      <Spacer size={32} fullWidth />

      {/* Form */}
      <form className={classes.formContainer}>
        <Slide
          in={slidingIn}
          timeout={500}
          direction={navigation.direction}
          unmountOnExit
          onExit={() => {
            formMethods.clearErrors()
          }}
          onExited={() => {
            navigation.setDirection(direction)
            history.push(destination)
          }}
        >
          <div>
            <Container>
              <Grid className={classes.container} container>
                {box(
                  <Grid container spacing={4}>
                    <EducationFields
                      editing
                      constantsData={constantsData}
                      control={control}
                      errors={formMethods.errors}
                      deleteField={onRemoveHandler}
                    />
                  </Grid>,
                  isLoading,
                )}
              </Grid>
            </Container>
          </div>
        </Slide>

        <Navigation
          handleExitOnClick={formMethods.handleSubmit(
            (data: EducationFormValues) => handleSaveExit(data),
            (errors) => handleSaveExit(undefined, errors),
          )}
          handlePrevOnClick={formMethods.handleSubmit(
            (data: EducationFormValues) => handlePrevClick(data),
            (errors) => handlePrevClick(undefined, errors),
          )}
          handleNextOnClick={formMethods.handleSubmit(
            (data: EducationFormValues) => handleNextClick(data),
            (errors) => handleNextClick(undefined, errors),
          )}
          prevPage="Work"
          nextPage="Contact"
          isSubmitting={isMutationSubmitting}
          prevDisable={isLoading || isMutationSubmitting}
          exitDisable={isLoading || isMutationSubmitting}
          nextDisable={isLoading || isMutationSubmitting}
        />
      </form>
    </>
  )
}

export default EducationForm
